import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  useCreateResourceRequest,
  useUpdateResourceRequest,
  useApproveResourceRequest,
  useDenyResourceRequest,
  ResourceRequest,
  ResourceRequestItem,
} from '../../hooks/incidentResourceRequestHooks';
import {
  selectUser,
  selectGroupGuid,
} from '../../../../slices/commonSelectors';
import { useActualIncidentId } from '../../hooks/commonHooks';
import {
  ResourceRequestFormFields,
  ApprovalStage,
} from '../utils/ResourceRequestTypes';
import {
  getCurrentStage,
  isRequestCompleted,
} from '../utils/ResourceRequestUtils';
import { useQueryClient } from '@tanstack/react-query';
import { useIncidentRoster } from '../../hooks/incidentRoster/useIncidentRoster';
import { toDatetimeLocalValue } from '../ics213rr/Ics213RrFormBody';

interface UseResourceRequestFormProps {
  initialRequirement?: {
    kind: string;
    type?: string;
    unit?: string;
    needed?: number;
    description?: string;
  };
  requestData?: ResourceRequest | null;
  onSuccess?: () => void;
}

export const useResourceRequestForm = ({
  initialRequirement,
  requestData,
  onSuccess,
}: UseResourceRequestFormProps) => {
  const currentUser = useSelector(selectUser) as any;
  const groupGuid = useSelector(selectGroupGuid) as string;
  const incidentId = useActualIncidentId();
  const { data: incidentRoster } = useIncidentRoster();
  const queryClient = useQueryClient();

  const createRequestMutation = useCreateResourceRequest();
  const updateRequestMutation = useUpdateResourceRequest();
  const approveRequestMutation = useApproveResourceRequest();
  const denyRequestMutation = useDenyResourceRequest();
  // Get resource type from initialRequirement or existing request
  const resourceKind = initialRequirement?.kind || requestData?.name || '';
  const resourceType = initialRequirement?.type || '';

  const form = useForm<ResourceRequestFormFields>({
    mode: 'onChange',
    defaultValues: {
      name: resourceKind,
      supply_sources: '',
      substitutes: '',
      requestor_name: currentUser?.user_name || '',
      requestor_position: '',
      // Single item fields
      kind: resourceKind,
      type: resourceType,
      quantity: initialRequirement?.needed || 1,
      priority: 'Routine',
      description: initialRequirement?.description || '',
      requested_reporting_location_id: '',
      requested_reporting_location_geolocation: null,
      use_custom_location: false,
      requested_reporting_datetime: undefined,
      // Source location fields
      use_custom_source_location: false,
      source_location_id: '',
      source_location_geolocation: null,
      work_assignment_ids: null,
      attachments: [],
    },
  });

  const { reset, setValue, watch } = form;

  const currentStage = requestData
    ? getCurrentStage(requestData.status, requestData)
    : 'unknown';

  const isCompleted = isRequestCompleted(requestData);

  // Effect to populate form with existing request data
  useEffect(() => {
    if (requestData) {
      // Extract first item details
      const firstItem =
        requestData.items && requestData.items.length > 0
          ? requestData.items[0]
          : null;

      reset({
        capability_enabled: requestData.capability_enabled || '',
        name: requestData.name || '',
        request_number: requestData.request_number || '',
        timestamp: requestData.timestamp
          ? toDatetimeLocalValue(requestData.timestamp)
          : '',
        supply_sources: requestData.supply_sources || '',
        substitutes: requestData.substitutes || '',
        requestor_name: requestData.requestor_name || '',
        requestor_position: requestData.requestor_position || '',
        attachments: requestData.attachments ?? [],

        section_chief_name: requestData.section_chief_name || '',
        section_chief_position: requestData.section_chief_position || '',
        section_chief_comments: requestData.section_chief_comments || '',

        resl_position: requestData.resl_position || '',
        resl_tac: requestData.resl_tac || false,
        resl_personnel: requestData.resl_personnel || false,
        resl_available: requestData.resl_available,
        resl_not_available: requestData.resl_not_available || false,
        resl_name: requestData.resl_name || '',
        resl_comments: requestData.resl_comments || '',

        logistics_items: requestData.logistics_items || [],
        logistics_order_number: requestData.logistics_order_number || '',
        logistics_eta: requestData.logistics_eta
          ? new Date(requestData.logistics_eta)
          : undefined,
        logistics_cost: requestData.logistics_cost,
        supplier_name: requestData.supplier_name || '',
        supplier_phone: requestData.supplier_phone || '',
        supplier_fax: requestData.supplier_fax || '',
        supplier_email: requestData.supplier_email || '',
        logistics_notes: requestData.logistics_notes || '',
        logistics_comments: requestData.logistics_comments || '',
        logistics_order_placed_by: requestData.logistics_order_placed_by || '',
        logistics_order_placed_by_other:
          requestData.logistics_order_placed_by_other || '',
        logistics_name: requestData.logistics_name || '',
        logistics_position: requestData.logistics_position || '',

        // Source location fields
        source_location_id: requestData.source_location_id || '',
        source_location_geolocation: requestData.source_location_geolocation
          ? (requestData.source_location_geolocation as any)
          : null,
        use_custom_source_location: !!requestData.source_location_geolocation,

        finance_name: requestData.finance_name || '',
        finance_position: requestData.finance_position || '',
        finance_comments: requestData.finance_comments || '',
        work_assignment_ids: requestData.work_assignment_ids || null,

        final_name: requestData.final_name || '',
        final_position: requestData.final_position || '',
        final_comments: requestData.final_comments || '',
        // Set item fields from first item if available
        kind: firstItem?.kind || requestData.name || resourceKind,
        type: firstItem?.type || resourceType || '',
        quantity: firstItem?.quantity || 1,
        priority: firstItem?.priority || 'Routine',
        description: firstItem?.description || '',
        requested_reporting_location_id:
          firstItem?.requested_reporting_location_id || '',
        requested_reporting_location_geolocation:
          firstItem?.requested_reporting_location_geolocation || null,
        use_custom_location:
          !!firstItem?.requested_reporting_location_geolocation,
        requested_reporting_datetime: firstItem?.requested_reporting_datetime
          ? new Date(firstItem.requested_reporting_datetime)
          : undefined,
      });
    }
  }, [requestData, reset, resourceKind, resourceType]);

  // Set requestor position from incident roster
  useEffect(() => {
    if (incidentRoster && currentUser && incidentId) {
      const userEntry = incidentRoster.find(
        (member) =>
          member.userId === currentUser.user_guid &&
          member.incidentId === incidentId &&
          member.active === 1
      );
      if (
        userEntry?.incidentPositions &&
        userEntry.incidentPositions.length > 0
      ) {
        setValue(
          'requestor_position',
          userEntry.incidentPositions.map((pos) => pos.positionName).join(', ')
        );
      }
    }
  }, [incidentRoster, currentUser, incidentId, setValue]);

  // Auto-populate approver names based on current stage
  useEffect(() => {
    if (currentUser && !!requestData && currentStage !== 'unknown') {
      // Small delay to ensure form is fully initialized
      const timer = setTimeout(() => {
        // Helper function to set stage user info
        const setStageUserInfo = (
          nameField: string,
          positionField?: string,
          userPosition?: string
        ) => {
          const currentName = watch(
            nameField as keyof ResourceRequestFormFields
          );
          if (!currentName || currentName === '') {
            setValue(
              nameField as keyof ResourceRequestFormFields,
              currentUser?.user_name || ''
            );
          }
          if (positionField) {
            const currentPosition = watch(
              positionField as keyof ResourceRequestFormFields
            );
            if (!currentPosition || currentPosition === '') {
              setValue(
                positionField as keyof ResourceRequestFormFields,
                userPosition || ''
              );
            }
          }
        };
        // Get user position from incident roster
        let userPosition = '';
        if (incidentRoster && incidentId) {
          const userEntry = incidentRoster.find(
            (member) =>
              member.userId === currentUser.user_guid &&
              member.incidentId === incidentId &&
              member.active === 1
          );
          userPosition =
            userEntry?.incidentPositions
              .map((pos) => pos.positionName)
              .join(', ') || '';
        }

        // Check each stage and populate if user is at that stage
        const stageConfig = {
          section_chief: {
            signatureField: 'section_chief_signature',
            nameField: 'section_chief_name',
            positionField: 'section_chief_position',
          },
          resl: {
            signatureField: 'resl_signature',
            nameField: 'resl_name',
          },
          logistics: {
            signatureField: 'logistics_signature',
            nameField: 'logistics_name',
            positionField: 'logistics_position',
          },
          finance: {
            signatureField: 'finance_signature',
            nameField: 'finance_name',
            positionField: 'finance_position',
          },
          final: {
            signatureField: 'final_signature',
            nameField: 'final_name',
            positionField: 'final_position',
          },
        };

        const config = stageConfig[currentStage as keyof typeof stageConfig];
        if (
          config &&
          (!requestData || !(requestData as any)[config.signatureField])
        ) {
          setStageUserInfo(
            config.nameField,
            'positionField' in config ? config.positionField : undefined,
            userPosition
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [
    currentStage,
    currentUser,
    setValue,
    watch,
    incidentRoster,
    incidentId,
    requestData,
  ]);

  // Handle form submission
  const handleSubmit = async (formData: ResourceRequestFormFields) => {
    // Validate required fields
    if (!formData.kind || !formData.type) {
      console.error('Resource kind and type are required');
      return;
    }

    try {
      if (requestData) {
        // Create item from form fields
        const items: ResourceRequestItem[] = [
          {
            kind: formData.kind,
            type: formData.type,
            unit: 'Each',
            quantity: formData.quantity,
            status: 'pending',
            priority: formData.priority || undefined,
            description: formData.description || undefined,
            requested_reporting_location_id:
              formData.requested_reporting_location_id || undefined,
            requested_reporting_location_geolocation:
              formData.requested_reporting_location_geolocation || null,
            requested_reporting_datetime: formData.requested_reporting_datetime
              ? formData.requested_reporting_datetime instanceof Date
                ? formData.requested_reporting_datetime.toISOString()
                : formData.requested_reporting_datetime
              : undefined,
          },
        ];

        const updateData = {
          name: formData.name,
          supply_sources: formData.supply_sources,
          substitutes: formData.substitutes,
          requestor_name: formData.requestor_name,
          requestor_position: formData.requestor_position,
          items,
          work_assignment_ids: formData.work_assignment_ids || null,
          attachments: formData.attachments ?? [],
        };

        await updateRequestMutation.mutateAsync({
          requestId: requestData.resource_request_id,
          data: updateData,
        });
      } else {
        // Create new request
        const items: ResourceRequestItem[] = [
          {
            kind: formData.kind,
            type: formData.type,
            unit: 'Each',
            quantity: formData.quantity,
            status: 'pending',
            priority: formData.priority || undefined,
            description: formData.description || undefined,
            requested_reporting_location_id:
              formData.requested_reporting_location_id || undefined,
            requested_reporting_location_geolocation:
              formData.requested_reporting_location_geolocation || null,
            requested_reporting_datetime: formData.requested_reporting_datetime
              ? formData.requested_reporting_datetime instanceof Date
                ? formData.requested_reporting_datetime.toISOString()
                : formData.requested_reporting_datetime
              : undefined,
          },
        ];

        const requestName = initialRequirement ? formData.name : formData.kind;

        const newRequest = {
          name: requestName,
          supply_sources: formData.supply_sources,
          substitutes: formData.substitutes,
          requestor_name: formData.requestor_name,
          requestor_position: formData.requestor_position,
          items,
          status: 'draft' as const,
          group_guid: groupGuid || '',
          incident_id: incidentId || '',
          user_guid: currentUser?.user_guid || '',
          work_assignment_ids: formData.work_assignment_ids || null,
          attachments: formData.attachments ?? [],
        };

        await createRequestMutation.mutateAsync(newRequest);
      }
      onSuccess?.();
    } catch (error) {
      console.error('Failed to save resource request:', error);
    }
  };

  const buildForm = (items?: ResourceRequestItem[][]) => {
    const formData: any = {};
    switch (currentStage) {
      case 'draft' as ApprovalStage:
        // No approver fields to populate at draft stage
        formData.requestor_name = watch('requestor_name');
        formData.requestor_position = watch('requestor_position');
        formData.requestor_signature = watch('requestor_name');
        formData.requestor_signature_timestamp = new Date();
        break;
      case 'section_chief' as ApprovalStage:
        formData.section_chief_name =
          watch('section_chief_name') || currentUser?.user_name;
        formData.section_chief_position = watch('section_chief_position');
        formData.section_chief_signature =
          currentUser?.user_name || watch('section_chief_name');
        formData.section_chief_signature_timestamp = new Date();
        formData.section_chief_comments = watch('section_chief_comments');
        break;

      case 'resl' as ApprovalStage:
        formData.resl_tac = watch('resl_tac');
        formData.resl_personnel = watch('resl_personnel');
        formData.resl_available = watch('resl_available');
        formData.resl_not_available = watch('resl_not_available');
        formData.resl_name = watch('resl_name') || currentUser?.user_name;
        formData.resl_signature = currentUser?.user_name || watch('resl_name');
        formData.resl_signature_timestamp = new Date();
        formData.resl_comments = watch('resl_comments');
        formData.resl_position = watch('resl_position');
        break;

      case 'logistics' as ApprovalStage:
        formData.logistics_order_number = watch('logistics_order_number');
        formData.logistics_eta = watch('logistics_eta');
        formData.logistics_cost = watch('logistics_cost');
        formData.supplier_name = watch('supplier_name');
        formData.supplier_phone = watch('supplier_phone');
        formData.supplier_fax = watch('supplier_fax');
        formData.supplier_email = watch('supplier_email');
        formData.logistics_items = items || watch('logistics_items');
        formData.logistics_notes = watch('logistics_notes');
        formData.logistics_comments = watch('logistics_comments');
        formData.logistics_order_placed_by = watch('logistics_order_placed_by');
        formData.logistics_order_placed_by_other = watch(
          'logistics_order_placed_by_other'
        );
        formData.logistics_name =
          watch('logistics_name') || currentUser?.user_name;
        formData.logistics_position = watch('logistics_position');
        formData.logistics_signature =
          currentUser?.user_name || watch('logistics_name');
        formData.logistics_signature_timestamp = new Date();

        // Include source location
        if (watch('use_custom_source_location')) {
          formData.source_location_geolocation = watch(
            'source_location_geolocation'
          );
        } else {
          formData.source_location_id = watch('source_location_id');
        }
        break;

      case 'finance' as ApprovalStage:
        formData.finance_name = watch('finance_name') || currentUser?.user_name;
        formData.finance_position = watch('finance_position');
        formData.finance_signature =
          currentUser?.user_name || watch('finance_name');
        formData.finance_signature_timestamp = new Date();
        formData.finance_comments = watch('finance_comments');
        break;
      case 'final' as ApprovalStage:
        formData.final_name = watch('final_name') || currentUser?.user_name;
        formData.final_position = watch('final_position');
        formData.final_signature =
          currentUser?.user_name || watch('final_name');
        formData.final_signature_timestamp = new Date();
        formData.final_comments = watch('final_comments');
        break;
    }
    return formData;
  };

  // Handle approval at current stage
  const handleApprove = async (items?: ResourceRequestItem[][]) => {
    if (!requestData || !currentUser) return;
    try {
      const approvalData = buildForm(items);
      await approveRequestMutation.mutateAsync({
        requestId: requestData.resource_request_id,
        approvalData,
      });
      onSuccess?.();
    } catch (error) {
      console.error('Failed to approve request:', error);
      throw error;
    }
  };

  // Handle approval at current stage
  const handleDeny = async () => {
    if (!requestData || !currentUser) return;
    try {
      const denyData = buildForm();
      await denyRequestMutation.mutateAsync({
        requestId: requestData.resource_request_id,
        denyData: denyData,
      });
      onSuccess?.();
    } catch (error) {
      console.error('Failed to deny request:', error);
    }
  };
  // Handle cancel request
  const handleCancel = async () => {
    if (!requestData) return;

    try {
      // Check if request can be cancelled (not already completed)
      if (requestData.status === 'completed') {
        console.error('Cannot cancel a completed request');
        return;
      }

      await updateRequestMutation.mutateAsync({
        requestId: requestData.resource_request_id,
        data: {
          status: 'cancelled',
          archived: true,
        },
      });

      // Invalidate all resource request queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['incident_resource_requests'],
      });

      onSuccess?.();
    } catch (error) {
      console.error('Failed to cancel request:', error);
    }
  };

  return {
    form,
    currentStage,
    isCompleted,
    isSubmitting:
      createRequestMutation.isPending ||
      updateRequestMutation.isPending ||
      approveRequestMutation.isPending,
    isCancelling: updateRequestMutation.isPending,
    handleSubmit: form.handleSubmit(handleSubmit),
    handleApprove,
    handleDeny,
    handleCancel,
  };
};
